import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import {
  Users as UsersIcon, Globe, Instagram, Linkedin, MapPin, Sparkles,
} from "lucide-react";

// First-sign-in onboarding modal — shown ONCE for accounts whose
// `directory_onboarding_completed` flag is explicitly false at
// account-creation time. Existing users were backfilled to true so
// they never see this.
//
// The modal is a hard block: no close button, ESC is captured,
// backdrop clicks do nothing. The only way out is Publish or Skip,
// both of which flip the flag server-side so it never fires again.

const DIR_CATEGORIES = ["food", "beverage", "supplement", "wellness", "beauty", "pet", "other"];
const DIR_STAGES = [
  ["idea", "Idea"], ["pre-launch", "Pre-launch"], ["launched", "Launched"],
  ["in-retail", "In retail"], ["scaling", "Scaling"],
];

export default function CommunityProfileOnboardingModal() {
  const { user, refresh, status } = useAuth();
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);

  // Only trigger for authenticated members whose flag is explicitly
  // false (undefined means the /auth/me refresh hasn't landed yet —
  // wait for it rather than flashing the modal open then closed).
  const shouldShow =
    status === "authenticated"
    && !!user
    && user.directory_onboarding_completed === false;

  // Seed the form once when the modal is about to open. Prefill from
  // any existing directory doc so a partial draft survives.
  useEffect(() => {
    if (!shouldShow) { setProfile(null); return; }
    if (profile !== null) return;
    const dir = user.directory || {};
    setProfile({
      brand: dir.brand || "", bio: dir.bio || "", city: dir.city || "",
      category: dir.category || "", stage: dir.stage || "",
      website: dir.website || "", instagram: dir.instagram || "",
      linkedin: dir.linkedin || "",
    });
  }, [shouldShow, user, profile]);

  // Body scroll-lock + ESC swallowing while the modal is open.
  useEffect(() => {
    if (!shouldShow) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const swallowEsc = (e) => {
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); }
    };
    window.addEventListener("keydown", swallowEsc, { capture: true });
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", swallowEsc, { capture: true });
    };
  }, [shouldShow]);

  if (!shouldShow || !profile) return null;

  const set = (k, v) => setProfile((p) => ({ ...p, [k]: v }));
  const canPublish = profile.brand.trim() && profile.category;

  const publish = async () => {
    setSaving(true);
    try {
      await api.put("/auth/profile/directory", { ...profile, published: true });
      await refresh();
      toast.success("You're live in the Members Directory");
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  const skip = async () => {
    setSaving(true);
    try {
      await api.post("/auth/onboarding/directory/skip");
      await refresh();
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  return createPortal(
    <div
      // z-[10500] to sit above the walkthrough prompt (z-[9999]).
      className="fixed inset-0 z-[10500] flex items-center justify-center p-4"
      data-testid="community-onboarding-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="community-onboarding-heading"
    >
      {/* Backdrop: dark tint + heavy blur that borrows the shell's tone
          instead of pure black. Backdrop clicks are intentionally NOT
          wired — the user must Publish or Skip. */}
      <div
        aria-hidden
        className="absolute inset-0 bg-shell-side/85 backdrop-blur-xl"
      />

      {/* Panel */}
      <div className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-3xl bg-white shadow-2xl ring-1 ring-shell-border">
        {/* Header — brand gradient bar for a warm first-touch */}
        <div className="relative bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border-b border-shell-border p-6 md:p-8">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-2xl bg-primary/10 text-primary grid place-items-center flex-shrink-0">
              <UsersIcon className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-primary font-bold flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Welcome to CPG Institute
              </p>
              <h2
                id="community-onboarding-heading"
                className="mt-1 font-display text-2xl md:text-3xl leading-tight"
              >
                Introduce your brand to the community
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                A quick profile helps other founders find you inside the Members Directory.
                Publish now to appear on the directory — or skip and set it up later
                from <span className="font-semibold text-foreground">My Presence → Community profile</span>.
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="p-6 md:p-8 grid gap-4 md:grid-cols-2">
          <LabeledInput
            label="Brand" required value={profile.brand} onChange={(v) => set("brand", v)}
            placeholder="Your brand name" testid="community-onboarding-brand"
          />
          <LabeledInput
            label="City" value={profile.city} onChange={(v) => set("city", v)}
            placeholder="Los Angeles, CA" icon={MapPin} testid="community-onboarding-city"
          />
          <SelectField
            label="Category" required value={profile.category} onChange={(v) => set("category", v)}
            options={DIR_CATEGORIES.map((c) => [c, c.charAt(0).toUpperCase() + c.slice(1)])}
            placeholder="Pick a category" testid="community-onboarding-category"
          />
          <SelectField
            label="Stage" value={profile.stage} onChange={(v) => set("stage", v)}
            options={DIR_STAGES} placeholder="Pick a stage" testid="community-onboarding-stage"
          />
          <div className="md:col-span-2">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">Short bio</p>
            <textarea
              rows={3}
              value={profile.bio}
              onChange={(e) => set("bio", e.target.value.slice(0, 400))}
              placeholder="One paragraph about your brand and what you're building right now."
              data-testid="community-onboarding-bio"
              className="w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary"
            />
            <p className="text-[10px] text-muted-foreground text-right mt-1">{profile.bio.length}/400</p>
          </div>
          <LabeledInput
            label="Website" value={profile.website} onChange={(v) => set("website", v)}
            placeholder="https://…" icon={Globe} testid="community-onboarding-website"
          />
          <LabeledInput
            label="Instagram" value={profile.instagram} onChange={(v) => set("instagram", v)}
            placeholder="@yourbrand" icon={Instagram} testid="community-onboarding-instagram"
          />
          <LabeledInput
            label="LinkedIn" value={profile.linkedin} onChange={(v) => set("linkedin", v)}
            placeholder="in/warren-ezra" icon={Linkedin} testid="community-onboarding-linkedin"
          />
        </div>

        {/* Footer — exactly two actions, per spec */}
        <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t border-shell-border p-4 md:p-6 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2">
          <button
            type="button"
            onClick={skip}
            disabled={saving}
            data-testid="community-onboarding-skip"
            className="text-sm font-semibold text-muted-foreground hover:text-foreground border border-shell-border rounded-full px-5 py-2 disabled:opacity-60"
          >
            Skip
          </button>
          <button
            type="button"
            onClick={publish}
            disabled={saving || !canPublish}
            data-testid="community-onboarding-publish"
            className="btn-primary text-sm disabled:opacity-60"
            title={!canPublish ? "Brand and category are required to publish" : ""}
          >
            {saving ? "Saving…" : "Publish to Directory"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function LabeledInput({ label, value, onChange, placeholder, icon: Icon, testid, required }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">
        {label}{required && <span className="text-primary"> *</span>}
      </p>
      <div className="relative">
        {Icon && <Icon className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground" />}
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testid}
          className={[
            "w-full rounded-lg border border-shell-border bg-white py-2 pr-3 text-sm focus:outline-none focus:border-primary",
            Icon ? "pl-9" : "pl-3",
          ].join(" ")}
        />
      </div>
    </div>
  );
}

function SelectField({ label, value, onChange, options, placeholder, testid, required }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">
        {label}{required && <span className="text-primary"> *</span>}
      </p>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary"
      >
        <option value="">{placeholder}</option>
        {options.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
      </select>
    </div>
  );
}
